import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

/**
 * Учёт активного времени в приложении.
 *
 * Правила «активности»:
 *   - вкладка visible (document.visibilityState === 'visible')
 *   - + window focused (есть document.hasFocus())
 * Когда оба истинны — тикает локальный счётчик секунд.
 *
 * Tick на бэк:
 *   - каждые 60 секунд накопленной активности → POST /api/v1/learning-time/tick { minutes: 1 }
 *   - на blur/hide с накопленным >= 30s → tick { minutes: 1 } и сброс
 *   - локальный сброс счётчика после каждого успешного tick
 *
 * Monтируется один раз в App (на залогиненного юзера). Идемпотентен:
 * повторный mount при HMR не вызывает дубль-тиков потому что используется
 * один setInterval(1000), а не несколько.
 */
export function useLearningTimeTracker() {
  const { isAuthenticated } = useAuth();
  const secondsRef = useRef(0);
  const lastTickRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const isActive = () => {
      if (typeof document === 'undefined') return false;
      if (document.visibilityState !== 'visible') return false;
      if (typeof document.hasFocus === 'function' && !document.hasFocus()) return false;
      return true;
    };

    const sendTick = async (minutes) => {
      if (minutes < 1) return;
      const m = Math.min(10, Math.max(1, Math.floor(minutes)));
      try {
        await axios.post('/api/v1/learning-time/tick', { minutes: m });
      } catch (e) {
        // Тикали, бэк ответил ошибкой/нет сети — молчим, попробуем на следующем интервале
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[learning-time] tick failed', e?.response?.status, e?.message);
        }
      }
    };

    // Каждую секунду — если активны, добавляем секунду; если набрали >= 60 — отправляем тик
    const intervalId = setInterval(() => {
      if (!isActive()) return;
      secondsRef.current += 1;
      const now = Date.now();
      // Защита от двойных тиков в течение секунды (HMR / strict mode duplicate effect)
      if (secondsRef.current >= 60 && now - lastTickRef.current > 5000) {
        const minutes = Math.floor(secondsRef.current / 60);
        secondsRef.current -= minutes * 60;
        lastTickRef.current = now;
        sendTick(minutes);
      }
    }, 1000);

    // На уход в фон / закрытие вкладки — досылаем накопленный partial если >= 30s
    const flushPartial = () => {
      if (secondsRef.current >= 30) {
        const now = Date.now();
        if (now - lastTickRef.current > 5000) {
          lastTickRef.current = now;
          sendTick(1);
          secondsRef.current = 0;
        }
      }
    };

    const onHide = () => { if (document.visibilityState === 'hidden') flushPartial(); };
    const onBlur = () => flushPartial();
    // beforeunload: используем sendBeacon для надёжности
    const onBeforeUnload = () => {
      if (secondsRef.current >= 30 && navigator.sendBeacon) {
        try {
          const blob = new Blob([JSON.stringify({ minutes: 1 })], { type: 'application/json' });
          // sendBeacon не подхватывает axios-баз, поэтому абсолютный путь
          navigator.sendBeacon('/api/v1/learning-time/tick', blob);
        } catch (_) { /* best-effort */ }
      }
    };

    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('blur', onBlur);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Сбрасывать счётчик не нужно — useRef переживёт unmount только при HMR;
      // на logout (isAuthenticated → false) accumulated теряются, это ОК.
    };
  }, [isAuthenticated]);
}
